import type { CityConfig } from "../city-template/types";

export const waukeganConfig: CityConfig = {
  city: "Waukegan",
  state: "Illinois",
  stateAbbr: "IL",
  slug: "waukegan",
  route: "/cities/waukegan",
  title: "Emergency Passport Waukegan IL | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Waukegan, Illinois — as fast as 24 hours. Fast passport renewal and new passport applications for Waukegan, Gurnee, North Chicago, and all Lake County residents. A+ BBB rated registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Waukegan, IL",
  heroSubtitle: "Lake County business trip to London with two weeks notice? Great Lakes Naval Station deployment with expired documents? Gurnee Mills shopping trip turns into urgent international travel? We help Waukegan residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier serving all of Lake County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Waukegan%20Illinois%20Lake%20Michigan%20shoreline%20with%20historic%20Genesee%20Theatre%20downtown%20district%2C%20Waukegan%20Harbor%20lighthouse%20and%20marina%2C%20modern%20municipal%20buildings%2C%20Lake%20County%20government%20center%2C%20warm%20early%20evening%20golden%20light%20reflecting%20off%20Lake%20Michigan%2C%20professional%20drone%20photography%20with%20soft%20natural%20tones%2C%20Midwest%20Great%20Lakes%20city%20scenic%20landscape&width=1800&height=700&seq=waukegan-hero-001&orientation=landscape",
  heroImageAlt: "Waukegan Illinois Lake Michigan shoreline with downtown district and harbor at golden hour",
  geoPosition: "42.3636;-87.8448",
  geoPlacename: "Waukegan, Illinois",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
    distance: "Approximately 45 minutes south of Waukegan via I-94 and I-90",
  },
  nearbyCitiesZips: [
    { name: "Waukegan", zips: "60085, 60087" },
    { name: "Gurnee", zips: "60031" },
    { name: "North Chicago", zips: "60064" },
    { name: "Zion", zips: "60099" },
    { name: "Mundelein", zips: "60060" },
    { name: "Libertyville", zips: "60048" },
    { name: "Grayslake", zips: "60030" },
    { name: "Round Lake", zips: "60073" },
    { name: "Vernon Hills", zips: "60061" },
    { name: "Lake Forest", zips: "60045" },
    { name: "Highland Park", zips: "60035" },
    { name: "Buffalo Grove", zips: "60089" },
    { name: "Wheeling", zips: "60090" },
    { name: "Antioch", zips: "60002" },
    { name: "Winthrop Harbor", zips: "60096" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Chicago Passport Agency — no 45-minute drive required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-anchor-line", title: "Naval Station Support", desc: "Serving Great Lakes Naval Station personnel with deployment-ready documents" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown Chicago required" },
  ],
  internalLinks: [
    { name: "Expedited passport in Evanston, IL", route: "/cities/evanston" },
    { name: "Emergency passport help in Chicago, IL", route: "/cities/chicago" },
    { name: "Urgent passport services in Highland Park, IL", route: "/states/illinois" },
    { name: "Illinois passport services overview", route: "/states/illinois" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Waukegan, Illinois remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes south via I-94) — without you making the trip. Waukegan residents can ship from any UPS or FedEx in Lake County.",
  fpcOfficeDistance: "Serving Waukegan, Gurnee, North Chicago, Zion, Mundelein, Libertyville, Grayslake, Round Lake, Vernon Hills, Lake Forest, Highland Park, Buffalo Grove, and all of Lake County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Waukegan and Lake County. From the Waukegan Harbor marinas and the historic Genesee Theatre downtown district to the Great Lakes Naval Station gates and the corporate campuses along Lake-Cook Road, from Gurnee Mills families to the lakefront communities of Zion and Winthrop Harbor — we serve all of northeastern Lake County and beyond.",
  nearbyCitiesIntro: "We serve Waukegan and all nearby communities including Gurnee, North Chicago, Zion, Mundelein, Libertyville, Grayslake, Round Lake, Vernon Hills, Lake Forest, Highland Park, Buffalo Grove, Wheeling, Antioch, and Winthrop Harbor. We also serve all of Lake County and northeastern Illinois communities.",
  howItWorksIntro: "Most Waukegan travelers complete everything online and ship documents overnight. No need to drive 45 minutes to downtown Chicago — our couriers handle the Chicago Passport Agency trip for you.",
  mistakesIntro: "Waukegan is Lake County's largest city with a major naval base, growing corporate corridor, and diverse international community. Here are the most common passport mistakes we see from Lake County travelers.",
  testimonialsIntro: "Waukegan and Lake County travelers — from Great Lakes Naval personnel to corporate professionals to international families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Waukegan, IL? Here are the most common ones we hear from Lake County travelers.",
  statePageRoute: "/states/illinois",
  statePageName: "Illinois",
  dmvWebsite: "https://www.ilsos.gov/facilities/facilitylist.html",
  dmvLabel: "Illinois Secretary of State — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 45 minutes to downtown Chicago", "Full day commitment — parking and downtown navigation"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "No drive to downtown Chicago required", "Perfect for Lake County corporate travel and family vacations", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Waukegan travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Chicago Passport Agency — 45 minutes away but you never make the trip.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency direct courier", "Lake County corporate and family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Waukegan adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Waukegan address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Waukegan residents and families — including Great Lakes Naval personnel, new citizens, and Lake County families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Waukegan residents who lost their passport before international travel or deployment.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Waukegan and Lake County families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Waukegan and Lake County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Waukegan IL", description: "Urgent passport processing for Waukegan Illinois travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Waukegan IL", description: "Fast passport renewal for Waukegan Illinois residents with a previously-issued passport." },
    { name: "First-Time Passport Waukegan IL", description: "New passport application services for Waukegan Illinois residents applying for their first passport." },
    { name: "Child Passport Waukegan IL", description: "Minor passport services for Waukegan Illinois families with children under 16." },
  ],
  testimonials: [
    {
      name: "Petty Officer Marcus D.",
      location: "Great Lakes Naval Station, IL",
      rating: 5,
      text: "Deployment orders to Japan came through with 6 days notice and my passport was expired. Fast Passport Center had my renewal in 38 hours. Shipped from UPS in Waukegan and got my documents back fast. Absolutely clutch for military families.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Maria G.",
      location: "Waukegan, IL",
      rating: 5,
      text: "Family emergency in Mexico City and my daughter's passport was expired. Fast Passport Center got it done in 2 days. The dedicated agent was incredible — walked me through everything while I was stressed. I'll never use a post office for passports again.",
      service: "Emergency Child Passport",
    },
    {
      name: "James R.",
      location: "Gurnee, IL",
      rating: 5,
      text: "Business trip to Frankfurt with 10 days notice. Fast Passport Center had my new passport in 4 days. I work near the Amhurst corporate park and the whole process was online. Didn't take one day off work. Game-changer for Lake County professionals.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "wkgn-1",
      name: "Waukegan Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1500 N McAree Rd",
      city: "Waukegan",
      state: "IL",
      zip: "60085",
      phone: "(847) 662-3675",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Waukegan, near McAree Road corridor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Waukegan acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to downtown Waukegan and Great Lakes Naval Station.",
      icon: "ri-mail-line",
    },
    {
      id: "wkgn-2",
      name: "Gurnee Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3850 Grand Ave",
      city: "Gurnee",
      state: "IL",
      zip: "60031",
      phone: "(847) 855-3755",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Gurnee, approximately 5 miles west of Waukegan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Gurnee, Gurnee Mills area. Full passport services. Near Six Flags corridor.",
      icon: "ri-mail-line",
    },
    {
      id: "wkgn-3",
      name: "Lake County Clerk — Waukegan",
      type: "County Clerk Passport Acceptance",
      address: "18 N County St",
      city: "Waukegan",
      state: "IL",
      zip: "60085",
      phone: "(847) 377-2400",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Downtown Waukegan, Lake County Government Complex",
      website: "https://www.lakecountyil.gov/",
      websiteLabel: "Lake County Clerk",
      notes: "Lake County Clerk passport acceptance. Good alternative to post office. Near the Lake County Courthouse.",
      icon: "ri-government-line",
    },
    {
      id: "wkgn-4",
      name: "North Chicago Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1913 Sheridan Rd",
      city: "North Chicago",
      state: "IL",
      zip: "60064",
      phone: "(847) 689-4900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North Chicago, approximately 3 miles south of Waukegan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Nearest facility to Great Lakes Naval Station. Serves military families and North Chicago residents.",
      icon: "ri-mail-line",
    },
    {
      id: "wkgn-5",
      name: "Libertyville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1529 N Milwaukee Ave",
      city: "Libertyville",
      state: "IL",
      zip: "60048",
      phone: "(847) 362-8770",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Libertyville, approximately 10 miles southwest of Waukegan",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves central Lake County. Good alternative for Vernon Hills, Mundelein and southern Waukegan residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 45 min drive to downtown Chicago", fpc: "No — ship from Waukegan" },
    { feature: "Parking & Navigation", agency: "Downtown Chicago parking costs", fpc: "Ship from your neighborhood" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Lake County travelers often discover passport issues days before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip — especially during summer when Lake County travel peaks.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services near Waukegan sometimes produce photos that meet ID requirements but fail strict passport standards. Photos must be exactly 2x2 inches on a white background, no shadows, neutral expression.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Lake County acceptance facilities. Military personnel especially need to verify which form applies to their situation.",
    },
    {
      icon: "ri-anchor-line",
      title: "Military Deployment Documentation",
      description: "Great Lakes Naval personnel deploying overseas sometimes forget to bring deployment orders to their appointment. Without proof of military travel, the Chicago agency cannot process emergency applications.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating the Trip to Chicago",
      description: "Many Waukegan residents attempt to drive to the Chicago Passport Agency in an emergency. The 45-minute drive plus downtown parking means a half-day lost. Our courier service is faster and far more reliable.",
    },
    {
      icon: "ri-checkbox-line",
      title: "Assuming Standard Processing is Fine",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Holiday travel from Lake County peaks November–January — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Waukegan area or Lake County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near downtown Waukegan or Gurnee." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors. Military documents verified by experienced agents." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to the Chicago Passport Agency — just 45 minutes away but you never make the trip." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Waukegan address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Waukegan, IL?", a: "Fast Passport Center offers emergency passport processing in Waukegan, Illinois in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (45 minutes south via I-94) — without you making the trip." },
    { q: "Does Waukegan have its own passport agency?", a: "No — Waukegan does not have a regional passport agency. The nearest facility is the Chicago Passport Agency (approximately 45 minutes south via I-94 and I-90). Fast Passport Center's courier service means Waukegan residents never need to make the trip." },
    { q: "Can I get an expedited passport in Gurnee, North Chicago, or Zion?", a: "Absolutely. Fast Passport Center serves all of Lake County including Gurnee, North Chicago, Zion, Mundelein, Libertyville, Grayslake, Round Lake, and all northern Chicago suburbs. Our service works the same way everywhere." },
    { q: "What airport do Waukegan travelers use for international flights?", a: "Chicago O'Hare International Airport (ORD) is the primary airport for Waukegan and Lake County travelers. O'Hare is approximately 35 minutes south via I-94 and I-294 and offers direct international flights worldwide. Chicago Midway (MDW) is also an option. Waukegan Regional Airport (UGN) serves private and charter flights." },
    { q: "Does Fast Passport Center serve Great Lakes Naval Station?", a: "Yes. Fast Passport Center serves military personnel and families at Naval Station Great Lakes. We understand deployment timelines, PCS orders, and military travel requirements. Our courier service means personnel don't need to take leave to go to the Chicago Passport Agency." },
    { q: "Where can I get passport photos in Waukegan?", a: "Walgreens (multiple Lake County locations), CVS, and UPS Store locations throughout Waukegan and Gurnee offer passport photos. FedEx Office in Vernon Hills also provides them. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Can I get a passport for my child in Waukegan?", a: "Yes. Fast Passport Center provides child passport services for minors under 16 throughout Waukegan and Lake County. Both parents or legal guardians must typically be present at the acceptance facility, and we guide you through the entire process." },
    { q: "What is the closest passport acceptance facility to Great Lakes?", a: "The North Chicago Post Office at 1913 Sheridan Rd (approximately 1 mile from the Naval Station gates) and the Waukegan Main Post Office at 1500 N McAree Rd serve Great Lakes personnel and families. Both offer full passport acceptance services." },
  ],
  photoLocations: [
    { name: "Walgreens — Waukegan", address: "1700 North Lewis Ave, Waukegan, IL 60085", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS — Waukegan", address: "3601 N Green Bay Rd, Waukegan, IL 60087", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Gurnee", address: "6342 Grand Ave, Gurnee, IL 60031", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Gurnee", address: "4001 Grand Ave, Gurnee, IL 60031", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "Illinois SOS — Waukegan", address: "617 S Green Bay Rd, Waukegan, IL 60085", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Illinois Secretary of State for driver's licenses, REAL ID, and vehicle registration. Online reservations strongly recommended.", featured: true },
    { name: "Illinois SOS — Libertyville", address: "342 Peterson Rd, Libertyville, IL 60048", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves central Lake County. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 35 minutes south via I-94 and I-294",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Primary international airport for Waukegan and Lake County. One of the world's busiest airports with direct flights to Europe, Asia, and the Americas. United and American Airlines hub.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: true,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "Approximately 55 minutes southeast via I-94 and I-55",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Southwest Airlines hub with domestic connections. Good for travelers flying Southwest to connect to international destinations.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Flight Info",
      featured: false,
    },
    {
      code: "UGN",
      name: "Waukegan Regional Airport",
      distance: "Approximately 10 minutes north of downtown Waukegan",
      address: "2601 W Plane Rest Dr, Waukegan, IL 60087",
      note: "Regional general aviation airport serving Lake County. Not a commercial hub, but used for private and charter flights.",
      flightUrl: "https://www.waukeganil.gov/airport",
      flightLabel: "UGN Info",
      featured: false,
    },
  ],
};